import { useRef } from 'react'
import styled from 'styled-components'
import matter from 'gray-matter'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { serialize } from 'next-mdx-remote/serialize'
import { MDXRemote } from 'next-mdx-remote'
import { useRouter } from 'next/router'
import Wrapper from 'common/wrapper'
import Navigation from 'components/navigation'
import Footer from 'common/footer'
import OpenGraph from 'common/open-graph'
import { H1, P as Text } from 'common/typography'
import { components, mdxComponents } from 'common/post/md-renderer'
import MDStyles from 'post/styles'
import Author from 'post/author'
import CTA from 'post/cta'
import ProductBanner from 'post/product-banner'
import LastBlogs from 'post/last-blogs'
// import ProgressBar from 'post/progress-bar'
import NewsletterInvitation from 'post/newsletter-invitation'
import { getFirstTenBlogs, getBlogDataService } from 'services/blog'
import CareerProgress from 'projects/landing-ilustrations/career-progrees'
import NewsletterGradient from 'common/newsletter/newsletter-gradient'
import NotFound from 'errors/not-found'
import blogFontFace from 'common/blog-fontface'

const Resume = styled(Text)`
  color: var(--textOpaque)!important;
  font-size: 21px!important;
  line-height: 34px!important;
  margin: 16px 0 32px 0;
  @media(min-width: 768px) {
    margin: 16px 0 48px 0;
  }
`

function Blog({
  slug, title, keywords, ogImage, body, cta, cta_link, isMDX, author, date, notFound, duration, resume, blogs, course,
}) {
  const post = useRef(null)
  const router = useRouter()
  const markDownContent = isMDX
    ? (
      <MDXRemote {...body} components={mdxComponents} />
    ) : (
      <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
        {body}
      </ReactMarkdown>
    )

  if (router.isFallback || notFound) return <NotFound />

  return (
    <>
      <OpenGraph
        title={title}
        url={`https://leonidasesteban.com/blog/${slug}`}
        keyword={keywords}
        description={resume}
        image={ogImage || `https://leonidasesteban.com/api/og?title=${title}`}
      />
      <Navigation positionMobile="fixed" positionDesktop="static" background="var(--sky)" />
      {/* <ProgressBar post={post.current} /> */}
      <Wrapper maxWidth={680}>
        <MDStyles ref={post}>
          <H1 id="title">{title}</H1>
          <Resume>{resume}</Resume>
          <Author {...author} date={date} title={title} resume={resume} duration={duration} />
          {markDownContent}
          {cta && cta_link && <CTA cta={cta} cta_link={cta_link} />}
        </MDStyles>
      </Wrapper>
      <ProductBanner course={course} />
      <LastBlogs blogs={blogs} />
      <NewsletterInvitation />
      <CareerProgress shapeWithColor />
      <NewsletterGradient
        title="Entérate de las últimas novedades"
        subtitle="Streamings, Noticias y Early Adopter bonus. Sé el primero en enterarte de todo."
        formName="blog"
        gradientDesktop="linear-gradient(179.54deg, #0A004E 0.39%, #3B1375 56.69%, #DB086F 115.78%)"
        gradientMobile="linear-gradient(179.54deg, #0A004E 0.64%, #3B1375 56.42%, #DB086F 113.61%)"
        urlForm="MUIEABfZIVHHAcLP6n-Uz_9MnYSSuQ5ScE4yvGBrP6BX0VcA8oTGJeCrQSmQGu4zTzenD9Brq0_aZ_PP-ijHxi63G6GxNhztzRWSsu1PKjLEpYapgfrlJZphxuUuGoO1zPNXmA0-A6re52B_nH2N-3PJfc2FD7K5XIGDCRYm-C7VBuPn_2Ae5cbCpOWrD_zxZymGbzYpE5Z0R5Zn"
      />
      <Footer />
    </>
  )
}


Blog.pageTheme = blogFontFace

export async function getStaticPaths() {
  const blogs = await getFirstTenBlogs()
  if (blogs.error) {
    return { paths: [], fallback: true }
  }
  const paths = []
  blogs.forEach(({ slug }) => paths.push({ params: { slug } }))
  return { paths, fallback: 'blocking' }
}

async function parseMd(isMDX, content) {
  // console.log(content)
  if (isMDX) {
    return serialize(content)
  }
  return content
}

export async function getStaticProps({ params: { slug } }) {
  const blogData = await getBlogDataService(slug)
  const blogs = await getFirstTenBlogs()
  const authors = await import('../../components/post/authors.json')
  const { data, content } = matter(blogData)
  if (!data.author) {
    return {
      props: {
        notFound: true,
      },
      revalidate: 1,
    }
  }
  const body = await parseMd(data.isMDX, content)
  const authorSlug = data.author.toLowerCase()
  const author = authors[authorSlug]
  const wordsCount = content.split(' ').length
  const duration = Math.round(wordsCount / 200)
  const ogImage = data?.og_image || null
  return {
    props: {
      ...data,
      duration,
      author,
      blogs,
      body,
      slug,
      ogImage,
    },
    revalidate: 60,
  }
}

export default Blog
